// @ts-check
import { loadStripe } from '@stripe/stripe-js';
import toStripeLocale from '../utils/stripeLocale';

/**
 * Zero-decimal currencies for Stripe charge amounts.
 * @see https://docs.stripe.com/currencies#zero-decimal
 * Excludes special cases (ISK, UGX) that still require * 100 for charges.
 * CLP is zero-decimal per Stripe and must be sent without the * 100 multiplier.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
]);

/**
 * @typedef {import('@stripe/stripe-js').StripeConstructorOptions['locale']} StripeLocale
 */

/**
 * @typedef {Object} StripeConfig
 * @property {Object} stripe
 * @property {boolean} stripe.enabled
 * @property {string} stripe.publicKey
 * @property {string} [stripe.stripeAccount]
 * @property {Object} [stripe.appearance] - Stripe Elements Appearance API config
 * @property {string} [locale] - App locale (e.g. from CurrentLang); normalized for Stripe.js
 */

/**
 * @typedef {Object} MountPaymentElementOptions
 * @property {number} [amount] - Amount in regular currency units.
 * @property {string} [currency] - ISO currency code.
 * @property {Object} [appearance] - Stripe Appearance API configuration.
 * @property {string} [locale] - App locale; normalized to StripeLocale on mount.
 */

/**
 * @typedef {Object} MountExpressCheckoutElementOptions
 * @property {Object} [paymentMethods] - Express Checkout Element `paymentMethods` config
 *   (e.g. `{ googlePay: 'always', applePay: 'never' }`). Passed through verbatim so the same
 *   method serves any wallet (Google Pay now, Apple Pay later) without engine changes.
 * @property {number} [amount] - Amount in regular currency units.
 * @property {string} [currency] - ISO currency code.
 * @property {Object} [appearance] - Stripe Appearance API configuration.
 * @property {string} [locale] - App locale; normalized to StripeLocale on mount.
 * @property {Function} [onClick] - Handler for the ECE `click` event (validation gate).
 * @property {Function} [onConfirm] - Handler for the ECE `confirm` event (payment execution).
 * @property {Function} [onCancel] - Handler for the ECE `cancel` event (user dismissed the sheet).
 * @property {Function} [onReady] - Handler for the ECE `ready` event (wallet availability).
 */

/**
 * Stripe engine factory for SDK-only PaymentElement integration.
 * @returns {Object} Stripe engine interface
 */
function Stripe() {
  let stripeInstance = null;
  let stripePromise = null;
  let elements = null;
  let paymentElement = null;
  // Dedicated Express Checkout Element (ECE) handles, kept apart from the PaymentElement (TDC)
  // state so mounting a wallet never tears down the card form, and vice versa.
  let expressElements = null;
  let expressCheckoutElement = null;
  let submitHandler = null;
  /** @type {StripeLocale} */
  let engineLocale = 'auto';

  const defaultAppearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#0570de',
      borderRadius: '4px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
  };

  /**
   * Returns a normalized Stripe error result.
   * @param {string} message - Fallback error message.
   * @returns {{ error: { message: string } }}
   */
  function createStripeError(message) {
    return { error: { message } };
  }

  /**
   * Extracts a human-readable message from different error shapes.
   * @param {unknown} error - Unknown error thrown by Stripe.js.
   * @param {string} fallbackMessage - Default message.
   * @returns {string}
   */
  function getErrorMessage(error, fallbackMessage) {
    if (typeof error === 'string' && error) return error;
    if (error && typeof error === 'object' && 'message' in error && error.message) {
      return String(error.message);
    }

    return fallbackMessage;
  }

  /**
   * Normalizes a currency code to the format expected by Stripe.js.
   * @param {string} currency - ISO currency code.
   * @returns {string}
   */
  function normalizeCurrency(currency) {
    return String(currency || '')
      .trim()
      .toLowerCase();
  }

  /**
   * Normalizes a display amount to Stripe minor units.
   * @param {number|string} amount - Amount in regular currency units.
   * @param {string} currency - ISO currency code.
   * @returns {number|null}
   */
  function normalizeAmount(amount, currency) {
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return null;

    const normalizedCurrency = normalizeCurrency(currency);
    if (!normalizedCurrency) return null;

    const multiplier = ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency) ? 1 : 100;

    return Math.round(parsedAmount * multiplier);
  }

  /**
   * Initializes the Stripe instance with the publishable key.
   * @param {StripeConfig} config - Whitelabel config containing stripe settings
   * @returns {void}
   */
  const initialize = (config) => {
    const { enabled, publicKey, stripeAccount } = config.stripe || {};
    if (!enabled || !publicKey) return;

    engineLocale = toStripeLocale(config.locale);
    const stripeConstructorOptions = { locale: engineLocale };

    if (stripeAccount) {
      stripeConstructorOptions.stripeAccount = stripeAccount;
    }

    stripePromise = loadStripe(publicKey, stripeConstructorOptions);
    stripePromise.then((stripe) => {
      stripeInstance = stripe;
    });
  };

  /**
   * Returns the Stripe instance (null if still loading)
   * @returns {Object|null} Stripe instance
   */
  function getStripe() {
    return stripeInstance;
  }

  /**
   * Waits for the Stripe SDK to finish loading.
   * @returns {Promise<Object|null>} Stripe instance
   */
  async function waitForStripe() {
    if (stripeInstance) return stripeInstance;
    if (stripePromise) {
      stripeInstance = await stripePromise;
      return stripeInstance;
    }
    return null;
  }

  /**
   * Returns the Stripe Elements instance
   * @returns {Object|null} Stripe Elements instance
   */
  function getElements() {
    return elements;
  }

  /**
   * Returns the Express Checkout Element's Elements instance (null before mounting the ECE).
   * Used by the wallet flows to create a ConfirmationToken from the ECE instead of the
   * PaymentElement.
   * @returns {Object|null} Stripe Elements instance backing the ECE
   */
  function getExpressElements() {
    return expressElements;
  }

  /**
   * Mounts a Stripe PaymentElement to the given DOM element.
   * @param {HTMLElement} domElement - DOM element to mount PaymentElement
   * @param {MountPaymentElementOptions} [options] - Stripe Elements initialization options.
   * @returns {Object|null} The mounted PaymentElement
   */
  function mountPaymentElement(domElement, options = {}) {
    if (!stripeInstance) return null;

    const { amount, currency, appearance = {}, locale } = options;
    const normalizedCurrency = normalizeCurrency(currency);
    const normalizedAmount = normalizeAmount(amount, currency);
    const stripeLocale = locale !== undefined ? toStripeLocale(locale) : engineLocale;

    if (!normalizedAmount || !normalizedCurrency) {
      console.warn('Stripe PaymentElement requires a valid amount and currency');
      return null;
    }

    const mergedAppearance = {
      ...defaultAppearance,
      ...appearance,
      variables: {
        ...defaultAppearance.variables,
        ...(appearance.variables || {}),
      },
    };

    if (paymentElement) {
      paymentElement.unmount();
      paymentElement = null;
    }

    elements = stripeInstance.elements({
      mode: 'payment',
      amount: normalizedAmount,
      currency: normalizedCurrency,
      appearance: mergedAppearance,
      locale: stripeLocale,
    });

    paymentElement = elements.create('payment', {
      wallets: {
        link: 'never',
        applePay: 'never',
        googlePay: 'never',
      },
      fields: {
        billingDetails: {
          address: 'if_required',
        },
      },
    });
    paymentElement.mount(domElement);
    return paymentElement;
  }

  /**
   * Unmounts and cleans up the PaymentElement.
   * @returns {void}
   */
  function unmountPaymentElement() {
    if (paymentElement) {
      paymentElement.unmount();
      paymentElement = null;
    }
    elements = null;
  }

  /**
   * Updates the amount and currency shown by the mounted Elements instance.
   * @param {number} newAmount - Amount in regular currency units.
   * @param {string} currency - ISO currency code.
   * @returns {Promise<void>|null}
   */
  function updateAmount(newAmount, currency) {
    if (!elements) return null;

    const normalizedCurrency = normalizeCurrency(currency);
    const normalizedAmount = normalizeAmount(newAmount, currency);

    if (!normalizedAmount || !normalizedCurrency) {
      console.warn('Stripe Elements updateAmount requires a valid amount and currency');
      return null;
    }

    return elements.update({ amount: normalizedAmount, currency: normalizedCurrency });
  }

  /**
   * Mounts a Stripe Express Checkout Element (ECE) to the given DOM element.
   * The ECE renders express wallets (Google Pay, Apple Pay, …); which wallets appear is driven
   * entirely by `paymentMethods`, so the same method serves any wallet without engine changes.
   * Reuses the already-initialized `stripeInstance`, inheriting `stripeAccount` (Connect) and the
   * engine locale without re-initializing the SDK.
   * @param {HTMLElement} domElement - DOM element to mount the ECE into.
   * @param {MountExpressCheckoutElementOptions} [options] - ECE initialization options.
   * @returns {Object|null} The mounted Express Checkout Element
   */
  function mountExpressCheckoutElement(domElement, options = {}) {
    if (!stripeInstance) return null;

    const {
      paymentMethods,
      amount,
      currency,
      appearance = {},
      locale,
      onClick,
      onConfirm,
      onCancel,
      onReady,
    } = options;
    const normalizedCurrency = normalizeCurrency(currency);
    const normalizedAmount = normalizeAmount(amount, currency);
    const stripeLocale = locale !== undefined ? toStripeLocale(locale) : engineLocale;

    if (!normalizedAmount || !normalizedCurrency) {
      console.warn('Stripe Express Checkout Element requires a valid amount and currency');
      return null;
    }

    const mergedAppearance = {
      ...defaultAppearance,
      ...appearance,
      variables: {
        ...defaultAppearance.variables,
        ...(appearance.variables || {}),
      },
    };

    if (expressCheckoutElement) {
      expressCheckoutElement.unmount();
      expressCheckoutElement = null;
    }

    expressElements = stripeInstance.elements({
      mode: 'payment',
      amount: normalizedAmount,
      currency: normalizedCurrency,
      appearance: mergedAppearance,
      locale: stripeLocale,
    });

    expressCheckoutElement = expressElements.create(
      'expressCheckout',
      paymentMethods ? { paymentMethods } : {},
    );

    if (typeof onClick === 'function') expressCheckoutElement.on('click', onClick);
    if (typeof onConfirm === 'function') expressCheckoutElement.on('confirm', onConfirm);
    if (typeof onCancel === 'function') expressCheckoutElement.on('cancel', onCancel);
    if (typeof onReady === 'function') expressCheckoutElement.on('ready', onReady);

    expressCheckoutElement.mount(domElement);
    return expressCheckoutElement;
  }

  /**
   * Unmounts and cleans up the Express Checkout Element without touching the PaymentElement.
   * @returns {void}
   */
  function unmountExpressCheckoutElement() {
    if (expressCheckoutElement) {
      expressCheckoutElement.unmount();
      expressCheckoutElement = null;
    }
    expressElements = null;
  }

  /**
   * Updates the amount and currency shown by the mounted Express Checkout Element.
   * @param {number} newAmount - Amount in regular currency units.
   * @param {string} currency - ISO currency code.
   * @returns {Promise<void>|null}
   */
  function updateExpressAmount(newAmount, currency) {
    if (!expressElements) return null;

    const normalizedCurrency = normalizeCurrency(currency);
    const normalizedAmount = normalizeAmount(newAmount, currency);

    if (!normalizedAmount || !normalizedCurrency) {
      console.warn(
        'Stripe Express Checkout updateExpressAmount requires a valid amount and currency',
      );
      return null;
    }

    return expressElements.update({ amount: normalizedAmount, currency: normalizedCurrency });
  }

  /**
   * Validates and submits the mounted Elements instance.
   * @returns {Promise<{error?: {message: string}}>}
   */
  async function submitElements() {
    if (!elements) {
      return createStripeError('Stripe Elements not initialized');
    }

    try {
      return await elements.submit();
    } catch (error) {
      return createStripeError(getErrorMessage(error, 'Unable to submit Stripe Elements'));
    }
  }

  /**
   * Creates a Stripe ConfirmationToken from an Elements instance.
   * The instance defaults to the PaymentElement's `elements` (TDC); wallet flows pass their own
   * via `options.elements` (e.g. the Express Checkout Element's `expressElements`).
   * @param {Object} [params] - Optional params forwarded to Stripe.js.
   * @param {Object} [options]
   * @param {Object} [options.elements] - Elements instance to tokenize from. Defaults to the
   *   PaymentElement instance when omitted.
   * @returns {Promise<{confirmationToken?: Object, error?: {message: string}}>}
   */
  async function createConfirmationToken(params = {}, { elements: targetElements } = {}) {
    const activeElements = targetElements || elements;

    if (!stripeInstance || !activeElements) {
      return createStripeError('Stripe not initialized');
    }

    try {
      return await stripeInstance.createConfirmationToken({ elements: activeElements, params });
    } catch (error) {
      return createStripeError(
        getErrorMessage(error, 'Unable to create Stripe confirmation token'),
      );
    }
  }

  /**
   * Handles any next action required by a confirmed PaymentIntent.
   * @param {string} clientSecret - Client secret returned by backend.
   * @returns {Promise<{error?: {message: string}}>}
   */
  async function handleNextAction(clientSecret) {
    if (!stripeInstance) {
      return createStripeError('Stripe not initialized');
    }

    if (!clientSecret) {
      return createStripeError('Stripe client secret is required');
    }

    try {
      return await stripeInstance.handleNextAction({ clientSecret });
    } catch (error) {
      return createStripeError(getErrorMessage(error, 'Unable to handle next Stripe action'));
    }
  }

  /**
   * Registers a submit handler to be called when the payment button is clicked.
   * @param {Function|null} handler - The submit handler function
   * @returns {void}
   */
  function setSubmitHandler(handler) {
    submitHandler = handler;
  }

  /**
   * Triggers the registered submit handler.
   * Called from Checkout.js when the Pay button is clicked.
   * @returns {void}
   */
  function submit() {
    if (submitHandler) {
      return submitHandler();
    }

    return undefined;
  }

  return {
    initialize,
    getStripe,
    waitForStripe,
    getElements,
    getExpressElements,
    mountPaymentElement,
    unmountPaymentElement,
    updateAmount,
    mountExpressCheckoutElement,
    unmountExpressCheckoutElement,
    updateExpressAmount,
    submitElements,
    createConfirmationToken,
    handleNextAction,
    setSubmitHandler,
    submit,
  };
}

const stripe = Stripe();
export default stripe;
